/* import __COLOCATED_TEMPLATE__ from './procedure-dependencies-modal.hbs'; */
/* RESPONSIBLE TEAM: team-knowledge-foundations */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import type IntlService from 'ember-intl/services/intl';
import type AppService from 'embercom/services/app-service';

export interface DependentProcedure {
  id: number;
  name: string;
}

export interface ContentConflict {
  content_id: number;
  content_type: number;
  dependent_procedures: DependentProcedure[];
}

export type ProcedureDependenciesAction = 'delete' | 'unpublish' | 'disable';

interface Args {
  confirmContext: {
    conflicts: ContentConflict[];
    action: ProcedureDependenciesAction;
  };
}

interface Signature {
  Args: Args;
}

interface ProcedureView extends DependentProcedure {
  url: string;
}

interface ConflictView {
  contentId: number;
  contentType: number;
  procedures: ProcedureView[];
}

export default class ProcedureDependenciesModal extends Component<Signature> {
  @service intl!: IntlService;
  @service intercomConfirmService: any;
  @service appService!: AppService;

  get action(): ProcedureDependenciesAction | undefined {
    return this.args.confirmContext?.action;
  }

  get conflicts(): ConflictView[] {
    let conflicts = this.args.confirmContext?.conflicts;
    if (!conflicts || conflicts.length === 0) {
      return [];
    }

    let appId = this.appService.app.id;
    return conflicts
      .filter((conflict) => conflict?.dependent_procedures)
      .map((conflict) => ({
        contentId: conflict.content_id,
        contentType: conflict.content_type,
        procedures: conflict.dependent_procedures.map((procedure) => ({
          ...procedure,
          url: `/a/apps/${appId}/automation/fin-ai-agent/procedures/${procedure.id}`,
        })),
      }));
  }

  get warning(): string {
    if (this.action === 'disable') {
      return this.intl.t('content-service.procedure-dependencies-modal.warning-disable');
    }
    return this.intl.t('content-service.procedure-dependencies-modal.warning', {
      action: this.action,
    });
  }

  get message(): string {
    let count = this.args.confirmContext?.conflicts?.length || 0;

    if (this.action === 'disable') {
      return this.intl.t('content-service.procedure-dependencies-modal.message-disable', {
        count,
      });
    }

    return this.intl.t('content-service.procedure-dependencies-modal.message', {
      count,
    });
  }

  get closeButtonText(): string {
    return this.intl.t('content-service.procedure-dependencies-modal.close');
  }

  @action closeModal() {
    this.intercomConfirmService.reject({ closed: true });
    this.intercomConfirmService.closeConfirm();
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'ContentService::ProcedureDependenciesModal': typeof ProcedureDependenciesModal;
    'content-service/procedure-dependencies-modal': typeof ProcedureDependenciesModal;
  }
}
